import { spawnEnemyBullet, type Bullet } from './bullet';

export interface FiringState {
  cooldownMs: number;
}

export interface EnemyForFiring {
  x: number;
  y: number;
  width: number;
  height: number;
  row: number;
  col: number;
}

export interface FiringConfig {
  level: number;
  minIntervalMs: number;
  maxIntervalMs: number;
  rng: () => number;
}

export interface TickFiringResult {
  state: FiringState;
  bulletsToSpawn: Bullet[];
}

function rollInterval(config: FiringConfig): number {
  const speedup = 1 / Math.max(1, config.level);
  const min = config.minIntervalMs * speedup;
  const max = config.maxIntervalMs * speedup;
  return min + config.rng() * (max - min);
}

export function createFiringState(config: FiringConfig): FiringState {
  return { cooldownMs: rollInterval(config) };
}

export function tickEnemyFiring(
  state: FiringState,
  enemies: readonly EnemyForFiring[],
  dtMs: number,
  config: FiringConfig,
): TickFiringResult {
  if (enemies.length === 0 || dtMs <= 0) {
    return { state, bulletsToSpawn: [] };
  }
  const newCooldown = state.cooldownMs - dtMs;
  if (newCooldown > 0) {
    return { state: { cooldownMs: newCooldown }, bulletsToSpawn: [] };
  }
  const shooter = pickShooter(enemies, config.rng);
  if (!shooter) {
    return { state, bulletsToSpawn: [] };
  }
  return {
    state: { cooldownMs: rollInterval(config) },
    bulletsToSpawn: [spawnEnemyBullet(shooter)],
  };
}

function pickShooter(
  enemies: readonly EnemyForFiring[],
  rng: () => number,
): EnemyForFiring | null {
  const bottomByCol = new Map<number, EnemyForFiring>();
  for (const e of enemies) {
    const cur = bottomByCol.get(e.col);
    if (!cur || e.row > cur.row) bottomByCol.set(e.col, e);
  }
  const candidates = Array.from(bottomByCol.values());
  if (candidates.length === 0) return null;
  const idx = Math.min(
    candidates.length - 1,
    Math.floor(rng() * candidates.length),
  );
  return candidates[idx] ?? null;
}
